import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { BOOKS_BY_IDS_API, BOOKS_FEED_API } from '../../api/endpoint';
import { Book, BookDetail } from '../../types';
import { IconButton, Typography, useMediaQuery } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Loader from '../../components/loader';
import axios from 'axios';
import axiosInstance from '../../api/axiosConfig';
import createAxiosInstance from '../../api/axiosConfig';
import { useSwipeable } from 'react-swipeable';

const avatars = [
  '/APP/BookAvatars/Avatar1.jpg',
  '/APP/BookAvatars/Avatar2.jpg',
  '/APP/BookAvatars/Avatar3.jpg',
  '/APP/BookAvatars/Avatar4.jpg',
  '/APP/BookAvatars/Avatar5.jpg'
];

const TopBooks = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [bookDetails, setBookDetails] = useState<BookDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<string | null>(null);
  const isFetching = useRef(false);
  const [activeSlide, setActiveSlide] = useState(2);
  const [stopExecution, setStopExecution] = useState<boolean | null>(false);

  // Media query for responsiveness
  const isMobile = useMediaQuery('(max-width:600px)');

  const slideWidth = isMobile ? 170 : 252;
  const slideHeight = isMobile ? 240 : 302;

  // Swipe handlers
  const handlers = useSwipeable({
    onSwipedLeft: () => next(),
    onSwipedRight: () => prev(),
    delta: 10,
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  const fetchBooks = async (pageToken: string | null = null, count = 5) => {
    if (!stopExecution && !isFetching.current) {
      isFetching.current = true;

      try {
        const axiosInstance = createAxiosInstance(true);
        const response = await axiosInstance.get(BOOKS_FEED_API, {
          params: { page: pageToken || '', count }
        });

        if (!response?.data?.data?.length) {
          setStopExecution(true);
          return;
        }

        setBooks((prevBooks) => [...prevBooks, ...response.data.data]);
        setCurrentPage(response.data.page);

        const bookIds = response.data.data.map((book: Book) => ({
          book_id: book.book_id,
          user_id: book.user_id
        }));

        if (bookIds.length > 0) {
          const detailsAxios = createAxiosInstance(false);
          const bookDetailsResponse = await detailsAxios.post(BOOKS_BY_IDS_API, { targetIds: bookIds });
          setBookDetails((prevBookDetails) => [...prevBookDetails, ...bookDetailsResponse.data.data]);
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setError(error.message);
        }
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    }
  };

  useEffect(() => {
    setBooks([]);
    setBookDetails([]);
    setLoading(true);
    setError(null);
    setCurrentPage(null);
    fetchBooks();
  }, []);

  useEffect(() => {
    if (activeSlide === bookDetails.length - 1) {
      fetchBooks(currentPage);
    }
  }, [activeSlide, currentPage]);

  if (error) {
    return (
      <Typography variant="h6" color="error">
        Error: {error}
      </Typography>
    );
  }

  const sliderData =
    bookDetails.length > 0
      ? bookDetails.map((bookDetail, index) => ({
          id: bookDetail?.book_id + index,
          bgColor: 'white',
          icon: (
            <img
              src={`${process.env.REACT_APP_DEFAULT_AVATAR}/${bookDetail?.user_id}/BOOKS/IMAGES/medium/${bookDetail?.cover?.[0]?.name}`}
              alt={bookDetail?.title}
              width={slideWidth}
              height={slideHeight}
              className="slide-img"
            />
          ),
          title: bookDetail?.title || 'Anonymous'
        }))
      : avatars.map((avatar, index) => ({
          id: `avatar-${index}`,
          bgColor: 'white',
          icon: (
            <img
              src={process.env.REACT_APP_DEFAULT_AVATAR + avatar}
              alt={`Avatar ${index + 1}`}
              width={slideWidth}
              height={slideHeight}
              className="slide-img"
            />
          ),
          title: ''
        }));

  const next = () => {
    if (activeSlide < bookDetails.length - 1 || activeSlide < sliderData.length - 1) {
      setActiveSlide(activeSlide + 1);
    }
  };

  const prev = () => {
    if (activeSlide > 0) {
      setActiveSlide(activeSlide - 1);
    }
  };

  const getStyles = (index: number) => {
    const screenWidth = window.innerWidth;
    const baseTranslateX = isMobile ? screenWidth * 0.17 : screenWidth * 0.17;
    const baseTranslateZ = isMobile ? screenWidth * 0.3 : screenWidth * 0.25;

    if (activeSlide === index) {
      return {
        opacity: 1,
        transform: 'translateX(0px) translateZ(0px) rotateY(0deg)',
        zIndex: 10
      };
    } else if (activeSlide - 1 === index) {
      return {
        opacity: 1,
        transform: `translateX(-${baseTranslateX}px) translateZ(-${baseTranslateZ}px) rotateY(35deg)`,
        zIndex: 9
      };
    } else if (activeSlide + 1 === index) {
      return {
        opacity: 1,
        transform: `translateX(${baseTranslateX}px) translateZ(-${baseTranslateZ}px) rotateY(-35deg)`,
        zIndex: 9
      };
    } else if (activeSlide - 2 === index) {
      return {
        opacity: 1,
        transform: `translateX(-${2 * baseTranslateX}px) translateZ(-${baseTranslateZ + 100}px) rotateY(35deg)`,
        zIndex: 8
      };
    } else if (activeSlide + 2 === index) {
      return {
        opacity: 1,
        transform: `translateX(${2 * baseTranslateX}px) translateZ(-${baseTranslateZ + 100}px) rotateY(-35deg)`,
        zIndex: 8
      };
    } else if (index < activeSlide - 2) {
      return {
        opacity: 0,
        transform: `translateX(-${2 * baseTranslateX}px) translateZ(-${baseTranslateZ + 100}px) rotateY(35deg)`,
        zIndex: 7
      };
    } else if (index > activeSlide + 2) {
      return {
        opacity: 0,
        transform: `translateX(${2 * baseTranslateX}px) translateZ(-${baseTranslateZ + 100}px) rotateY(-35deg)`,
        zIndex: 7
      };
    }
  };

  const goToSlide = (index: number) => {
    setActiveSlide(index);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: isMobile ? '3rem 0' : '7rem 0'
      }}>
      <Typography
        sx={{
          color: 'white',
          letterSpacing: isMobile ? 1 : 3,
          mb: isMobile ? 5 : 5,
          fontFamily: 'Baloo2',
          fontSize: isMobile ? '28px' : '52px',
          fontWeight: 800
        }}>
        Top Books
      </Typography>
      {loading ? (
        <Loader />
      ) : (
        <Box sx={{ width: '100%', maxWidth: '80%' }}>
          <div className="slideC" {...handlers} style={{ width: slideWidth, height: slideHeight }}>
            {sliderData.map((item, i) => (
              <div
                key={item.id}
                className="slide"
                style={{
                  width: slideWidth,
                  height: slideHeight,
                  boxShadow: `0 5px 20px ${item.bgColor}30`,
                  ...getStyles(i)
                }}
                onClick={() => goToSlide(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    goToSlide(i);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Go to slide ${i + 1}`}>
                <div className="sliderContent">{item.icon}</div>
                <div style={{ margin: '20px 0', fontSize: '13px', fontFamily: 'Baloo2', fontWeight: '700' }}>
                  {item.title && activeSlide == i && (
                    <Typography color="white" align="center">
                      {item.title}
                    </Typography>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Box>
      )}
    </Box>
  );
};

export default TopBooks;
